import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getClinicInformations,
  downloadClinicInformationsCsv,
  type ClinicInformationListParams,
} from '@/services/admin/clinicInformations.ts';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { CSV_ERROR_MESSAGE, CSV_SUCCESS_MESSAGE } from '@/constants/messages';

export type {
  ClinicInformationListParams,
  ClinicInformationListResponse,
} from '@/services/admin/clinicInformations.ts';

export const useAdminClinicInformationsList = (
  site: string | null,
  params: ClinicInformationListParams
) => {
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['admin', 'clinic-informations', site ?? '', params],
    queryFn: () => getClinicInformations(site!, params),
    enabled: !!site,
    /** Override global staleTime: refetch on every 検索 click. */
    staleTime: 0,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
};

export const useAdminClinicInformationsCsvDownload = () => {
  const toast = useAppToast();

  const mutation = useMutation({
    mutationFn: ({ site, params }: { site: string; params: ClinicInformationListParams }) =>
      downloadClinicInformationsCsv(site, params),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, CSV_ERROR_MESSAGE));
    },
  });

  const downloadCsv = async (site: string, params: ClinicInformationListParams) => {
    await mutation.mutateAsync({ site, params });
  };

  return { downloadCsv, isDownloading: mutation.isPending };
};

